/**
 * @file Utility functions for the Tablespoon Agency website
 * @description 
 * This file contains utility functions used throughout the application,
 * including class name merging, responsive helper functions, and other common utilities.
 * 
 * Key utilities:
 * - Class name merging with cn()
 * - Responsive/adaptive utilities
 * - Type helpers
 * - Media query breakpoint constants
 * 
 * @dependencies
 * - clsx: For conditional class name construction
 * - tailwind-merge: For handling conflicting Tailwind classes
 */

import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merges multiple class name values into a single string, handling Tailwind class conflicts
 * @param inputs - Class values to be merged (strings, objects, arrays, etc.)
 * @returns Merged class string with resolved Tailwind conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert a string to kebab-case
 * @param str - String to convert
 * @returns Kebab-cased string
 */
export function toKebabCase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase()
}

/**
 * Convert a string to camelCase
 * @param str - String to convert
 * @returns Camel-cased string
 */
export function toCamelCase(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (letter, index) => 
      index === 0 ? letter.toLowerCase() : letter.toUpperCase()
    )
    .replace(/[\s-_]+/g, '')
}

/**
 * Format a number with commas as thousands separators
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

/**
 * Truncate a string to a specified length and add an ellipsis
 * @param str - String to truncate
 * @param length - Maximum length before truncation
 * @param ending - Custom ending for truncated string (default: "...")
 * @returns Truncated string
 */
export function truncateString(str: string, length: number, ending: string = "..."): string {
  if (str.length <= length) {
    return str
  }
  return str.substring(0, length - ending.length) + ending
}

/**
 * Format a date to a readable string
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | number, 
  options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }
): string {
  const dateObj = typeof date === 'object' ? date : new Date(date)
  return new Intl.DateTimeFormat('en-US', options).format(dateObj)
}

/**
 * Simple debounce function to prevent rapid function calls
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  
  return function(...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Get viewport dimensions
 * @returns Object with viewport width and height
 */
export function getViewportDimensions(): { width: number; height: number } {
  // Default to reasonable values for SSR
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768 }
  }
  
  return {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

/**
 * Constants for responsive breakpoints (matching Tailwind config)
 */
export const BREAKPOINTS = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
}

/**
 * Check if an element has overflow
 * @param element - DOM element to check
 * @returns Boolean indicating if element has overflow
 */
export function hasOverflow(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight || 
         element.scrollWidth > element.clientWidth
}

/**
 * Type guard for checking if value is not null or undefined
 * @param value - Value to check
 * @returns boolean indicating if value is not null or undefined
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

/**
 * Get a nested property from an object using a path string
 * @param obj - Object to get property from
 * @param path - Path to property (e.g. "user.profile.name")
 * @param defaultValue - Default value if property doesn't exist
 * @returns Property value or default
 */
export function getNestedValue<T>(
  obj: Record<string, any>, 
  path: string, 
  defaultValue: T
): T {
  const keys = path.split('.')
  let current = obj
  
  for (const key of keys) {
    if (current === undefined || current === null) {
      return defaultValue
    }
    current = current[key]
  }
  
  return (current as T) ?? defaultValue
}